// AvatarMenu — topbar dropdown that owns the Advanced Mode toggle (R5.1)
// and the logout action. Replaces the bare logout icon in AdminLayout
// because task 2.3 specifies the Advanced Mode switch, description, and
// keyboard hint live in the avatar dropdown.
//
// Interaction model mirrors RoleSwitcher:
//   - Click avatar → opens menu. Click outside or press Escape → closes.
//   - Arrow keys move focus between menu items.
//   - The Advanced Mode toggle is a labeled switch (role="switch") with a
//     caption describing what it reveals and a keyboard hint for
//     Cmd/Ctrl+Shift+A. The global shortcut already lives in
//     AdvancedModeContext; the menu just surfaces it.
//
// The menu is positioned the same way as RoleSwitcher's popover — absolute,
// right-aligned under the topbar trigger — so the two can coexist.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAdvancedMode } from "@/context/AdvancedModeContext";
import { useSession } from "@/session";

export interface AvatarMenuProps {
  /**
   * Invoked when the operator selects Log out. AvatarMenu does not own
   * the network call; AdminLayout already has the logout plumbing.
   */
  onLogout: () => void;
  /** Mirrors the disabled state of the former logout button. */
  loggingOut: boolean;
}

/** Platform-aware hint label for the Advanced Mode keyboard shortcut. */
function shortcutHint(): string {
  const isMac =
    typeof navigator !== "undefined" &&
    /Mac|iPhone|iPad|iPod/i.test(navigator.platform ?? navigator.userAgent);
  return isMac ? "⌘⇧A" : "Ctrl+Shift+A";
}

/**
 * Two-letter initials from an alias. Falls back to a generic glyph when
 * the alias is empty (e.g. pre-auth loading states).
 */
function initialsFor(alias: string | undefined): string {
  if (!alias) return "•";
  const trimmed = alias.trim();
  if (trimmed.length === 0) return "•";
  // Aliases are single-token usernames (e.g. "raghuvc"); take the first
  // two letters for a compact chip.
  return trimmed.slice(0, 2).toUpperCase();
}

export function AvatarMenu({ onLogout, loggingOut }: AvatarMenuProps): JSX.Element {
  const { session } = useSession();
  const { isAdvanced, toggle } = useAdvancedMode();
  const [open, setOpen] = useState<boolean>(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const alias = session?.alias;
  const label = alias ? `Account menu for ${alias}` : "Account menu";
  const hint = useMemo<string>(() => shortcutHint(), []);

  // Close on outside click / Escape — same pattern as RoleSwitcher.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleToggleAdvanced = useCallback((): void => {
    // Keep the menu open after toggling so the operator can see the state
    // change reflected in the switch — matches the Cmd+Shift+A experience
    // where they don't lose their place.
    toggle();
  }, [toggle]);

  const handleLogout = useCallback((): void => {
    setOpen(false);
    onLogout();
  }, [onLogout]);

  const handleMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const items = Array.from(
      e.currentTarget.querySelectorAll<HTMLElement>("[data-avatar-menu-item]"),
    );
    const idx = items.findIndex((b) => b === document.activeElement);
    if (idx < 0) {
      items[0]?.focus();
      return;
    }
    const delta = e.key === "ArrowDown" ? 1 : -1;
    const next = items[(idx + delta + items.length) % items.length];
    next?.focus();
  };

  return (
    <div className="avatar-menu" ref={rootRef}>
      <button
        type="button"
        className="avatar-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        ref={triggerRef}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="avatar-menu__initials" aria-hidden="true">
          {initialsFor(alias)}
        </span>
      </button>
      {open && (
        <div
          className="avatar-menu__popover"
          role="menu"
          aria-label="Account and preferences"
          onKeyDown={handleMenuKeyDown}
        >
          {alias && (
            <div className="avatar-menu__header" role="presentation">
              <div className="avatar-menu__header-label">Signed in as</div>
              <div className="avatar-menu__header-alias mono">{alias}</div>
            </div>
          )}
          <div className="avatar-menu__section" role="presentation">
            <button
              type="button"
              className="avatar-menu__toggle"
              role="switch"
              aria-checked={isAdvanced}
              data-avatar-menu-item
              onClick={handleToggleAdvanced}
            >
              <span className="avatar-menu__toggle-text">
                <span className="avatar-menu__toggle-label">Advanced mode</span>
                <span className="avatar-menu__toggle-desc">
                  Show raw IDs, JSON, rule_id, raznx_* fields
                </span>
              </span>
              <span className="avatar-menu__toggle-controls">
                <span
                  className={
                    isAdvanced
                      ? "avatar-menu__switch avatar-menu__switch--on"
                      : "avatar-menu__switch"
                  }
                  aria-hidden="true"
                >
                  <span className="avatar-menu__switch-thumb" />
                </span>
                <span className="avatar-menu__kbd" aria-hidden="true">
                  {hint}
                </span>
              </span>
            </button>
          </div>
          <div className="avatar-menu__divider" role="separator" />
          <button
            type="button"
            className="avatar-menu__item"
            role="menuitem"
            data-avatar-menu-item
            onClick={handleLogout}
            disabled={loggingOut}
            aria-busy={loggingOut || undefined}
          >
            {loggingOut ? "Logging out…" : "Log out"}
          </button>
        </div>
      )}
    </div>
  );
}
